import { QueryClient } from "@tanstack/react-query";
import { MethodForModel, ModelName, ParamsForMethod, QueryDataForMethod, TellescopeQueryKey } from "../../types";
import { buildTellescopeQueryKey } from "../../keys";

/**
 * Arguments for getting query state from cache.
 *
 * @template Model - The Tellescope model name
 * @template Method - The query method for the model
 */
export interface GetTellescopeQueryStateArgs<Model extends ModelName, Method extends MethodForModel<Model>> {
  /** The Tellescope model name */
  model: Model;
  /** The query method to call on the model */
  method: Method;
  /** The request parameters for the query */
  request: Partial<ParamsForMethod<Model, Method>>;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
}

/**
 * Gets the query state from the query client for a Tellescope query.
 * Returns `undefined` if the query does not exist in the cache.
 * The query state includes information about the query's status, data, error, and more.
 *
 * @param args - The get query state arguments
 * @returns The query state or undefined
 *
 * @example
 * ```tsx
 * // Get query state
 * const state = getTellescopeQueryState({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "user-123" },
 * });
 *
 * if (state) {
 *   console.log("Status:", state.status); // "pending" | "error" | "success"
 *   console.log("Is fetching:", state.isFetching);
 *   console.log("Is stale:", state.isStale);
 *   console.log("Data:", state.data);
 *   console.log("Error:", state.error);
 * }
 * ```
 */
export const getTellescopeQueryState = <Model extends ModelName, Method extends MethodForModel<Model>, TError = Error>({
  model,
  method,
  request,
  queryClient,
}: GetTellescopeQueryStateArgs<Model, Method>) => {
  const queryKey = buildTellescopeQueryKey({ model, method, request });
  return queryClient.getQueryState<QueryDataForMethod<Model, Method>, TError, TellescopeQueryKey<Model, Method>>(
    queryKey
  );
};
